// The single source of truth for every SVG glyph in the frontend. All icons are 24×24
// stroke-line glyphs inheriting `currentColor`; `size` is optional — omit it to size via CSS
// (class/stylex props spread onto the <svg> root). No inline <svg> belongs anywhere else.
import type { JSX } from 'preact';

export interface IconProps extends Omit<JSX.SVGAttributes<SVGSVGElement>, 'size'> {
  /** Pixel width+height. Omit to size via CSS. */
  size?: number | string;
  strokeWidth?: number;
}

type Props = IconProps & { children?: JSX.Element | JSX.Element[] };

const I = ({ size, strokeWidth = 2, children, ...rest }: Props) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={strokeWidth}
    strokeLinecap="round"
    strokeLinejoin="round"
    {...(size != null ? { width: size, height: size } : {})}
    {...rest}
  >
    {children}
  </svg>
);

// ── brand ────────────────────────────────────────────────────────────────────
export const BrandMarkIcon = (p: IconProps) => (
  <I strokeWidth={2.2} {...p}>
    <path d="M12 22V12M12 12c0-4 2-7 6-8-1 4-2 7-6 8zM12 12C8 11 6 8 6 4c4 1 6 4 6 8z" />
  </I>
);

// ── navigation / sections ────────────────────────────────────────────────────
export const MapperIcon = (p: IconProps) => (
  <I {...p}>
    <circle cx="18" cy="5" r="3" />
    <circle cx="6" cy="12" r="3" />
    <circle cx="18" cy="19" r="3" />
    <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" />
    <line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
  </I>
);
export const ChipIcon = (p: IconProps) => (
  <I {...p}>
    <rect x="4" y="4" width="16" height="16" rx="2" />
    <rect x="9" y="9" width="6" height="6" />
    <path d="M9 1v3M15 1v3M9 20v3M15 20v3M1 9h3M1 15h3M20 9h3M20 15h3" />
  </I>
);
export const SlidersIcon = (p: IconProps) => (
  <I {...p}>
    <line x1="4" y1="21" x2="4" y2="14" />
    <line x1="4" y1="10" x2="4" y2="3" />
    <line x1="12" y1="21" x2="12" y2="12" />
    <line x1="12" y1="8" x2="12" y2="3" />
    <line x1="20" y1="21" x2="20" y2="16" />
    <line x1="20" y1="12" x2="20" y2="3" />
    <line x1="1" y1="14" x2="7" y2="14" />
    <line x1="9" y1="8" x2="15" y2="8" />
    <line x1="17" y1="16" x2="23" y2="16" />
  </I>
);
export const UsersIcon = (p: IconProps) => (
  <I {...p}>
    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" />
  </I>
);
export const HomeIcon = (p: IconProps) => (
  <I {...p}>
    <path d="M3 9.5 12 3l9 6.5V20a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
    <path d="M9 22v-8h6v8" />
  </I>
);
export const MonitorIcon = (p: IconProps) => (
  <I {...p}>
    <rect x="2" y="3" width="20" height="14" rx="2" />
    <path d="M8 21h8M12 17v4" />
  </I>
);
export const UserIcon = (p: IconProps) => (
  <I {...p}>
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
    <circle cx="12" cy="7" r="4" />
  </I>
);
export const ScanIcon = (p: IconProps) => (
  <I {...p}>
    <path d="M3 7V5a2 2 0 0 1 2-2h2" />
    <path d="M17 3h2a2 2 0 0 1 2 2v2" />
    <path d="M21 17v2a2 2 0 0 1-2 2h-2" />
    <path d="M7 21H5a2 2 0 0 1-2-2v-2" />
    <path d="M7 12h10" />
  </I>
);
export const SettingsIcon = (p: IconProps) => (
  <I {...p}>
    <circle cx="12" cy="12" r="3" />
    <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
  </I>
);

export const LayersIcon = (p: IconProps) => (
  <I {...p}>
    <path d="M12 2 2 7l10 5 10-5-10-5z" />
    <path d="M2 17l10 5 10-5M2 12l10 5 10-5" />
  </I>
);
export const MessageSquareIcon = (p: IconProps) => (
  <I {...p}>
    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
    <path d="M13 8H7M17 12H7" />
  </I>
);
export const ScrollTextIcon = (p: IconProps) => (
  <I {...p}>
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    <path d="M14 2v6h6" />
    <path d="M16 13H8M16 17H8M10 9H8" />
  </I>
);

// ── farm entities ────────────────────────────────────────────────────────────
export const GridIcon = (p: IconProps) => (
  <I {...p}>
    <rect x="3" y="3" width="7" height="7" rx="1" />
    <rect x="14" y="3" width="7" height="7" rx="1" />
    <rect x="3" y="14" width="7" height="7" rx="1" />
    <rect x="14" y="14" width="7" height="7" rx="1" />
  </I>
);
/** Calendar — the grow scheduler's timeline (W3/R-164). Same 24×24, stroke-2,
 * round-cap construction as the rest of the set. */
export const CalendarIcon = (p: IconProps) => (
  <I {...p}>
    <rect x="3" y="5" width="18" height="16" rx="2" />
    <path d="M3 10h18" />
    <path d="M8 3v4" />
    <path d="M16 3v4" />
  </I>
);
export const DropletIcon = (p: IconProps) => (
  <I {...p}>
    <path d="M12 2.7 6.3 9a8 8 0 1 0 11.4 0z" />
  </I>
);
export const RackIcon = (p: IconProps) => (
  <I {...p}>
    <rect x="3" y="3" width="18" height="18" rx="2" />
    <path d="M3 9h18M3 15h18" />
  </I>
);
export const WrenchIcon = (p: IconProps) => (
  <I {...p}>
    <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z" />
  </I>
);

export const LeafIcon = (p: IconProps) => (
  <I {...p}>
    <path d="M11 20A7 7 0 0 1 4 13c0-5 4-9 10-10 4-.7 6 1 6 1s1.7 2 1 6c-1 6-5 10-10 10z" />
    <path d="M5 21c3-3 6-5 10-6" />
  </I>
);

// ── actions ──────────────────────────────────────────────────────────────────
export const PlusIcon = (p: IconProps) => (
  <I {...p}>
    <path d="M12 5v14M5 12h14" />
  </I>
);
export const TrashIcon = (p: IconProps) => (
  <I {...p}>
    <path d="M3 6h18M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2m3 0v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6" />
    <path d="M10 11v6M14 11v6" />
  </I>
);
export const PencilIcon = (p: IconProps) => (
  <I {...p}>
    <path d="M12 20h9" />
    <path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z" />
  </I>
);
export const UnlinkIcon = (p: IconProps) => (
  <I {...p}>
    <path d="M18.84 12.25 21 10.09a3.86 3.86 0 0 0-5.46-5.46l-2.16 2.16M5.16 11.75 3 13.91a3.86 3.86 0 0 0 5.46 5.46l2.16-2.16M2 2l20 20" />
  </I>
);
export const CopyIcon = (p: IconProps) => (
  <I {...p}>
    <rect x="9" y="9" width="13" height="13" rx="2" />
    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
  </I>
);
// Attach / choose a file (the firmware Upload dialog's file picker).
export const PaperclipIcon = (p: IconProps) => (
  <I {...p}>
    <path d="m21.44 11.05-9.19 9.19a6 6 0 0 1-8.49-8.49l8.57-8.57A4 4 0 1 1 18 8.84l-8.59 8.57a2 2 0 0 1-2.83-2.83l8.49-8.48" />
  </I>
);
export const LogoutIcon = (p: IconProps) => (
  <I {...p}>
    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4M16 17l5-5-5-5M21 12H9" />
  </I>
);

// ── arrows / chrome ──────────────────────────────────────────────────────────
export const ChevronRightIcon = (p: IconProps) => (
  <I {...p}>
    <path d="m9 18 6-6-6-6" />
  </I>
);
export const ChevronDownIcon = (p: IconProps) => (
  <I {...p}>
    <path d="m6 9 6 6 6-6" />
  </I>
);
export const ArrowLeftIcon = (p: IconProps) => (
  <I {...p}>
    <path d="M19 12H5M12 19l-7-7 7-7" />
  </I>
);
export const ArrowUpIcon = (p: IconProps) => (
  <I {...p}>
    <path d="M12 19V5M5 12l7-7 7 7" />
  </I>
);
// The Table's "more rows below" affordance — ArrowUp mirrored on the same box.
export const ArrowDownIcon = (p: IconProps) => (
  <I {...p}>
    <path d="M12 5v14M19 12l-7 7-7-7" />
  </I>
);
// R-239: the topbar's force-refresh. Two arcs with arrowheads — the conventional
// "reload" glyph, drawn on the same 24-box with the same stroke as its neighbours.
export const RefreshIcon = (p: IconProps) => (
  <I {...p}>
    <path d="M21 12a9 9 0 0 1-9 9 9 9 0 0 1-7.5-4M3 12a9 9 0 0 1 9-9 9 9 0 0 1 7.5 4" />
    <path d="M21 3v5h-5M3 21v-5h5" />
  </I>
);
export const CheckIcon = (p: IconProps) => (
  <I {...p}>
    <path d="M20 6 9 17l-5-5" />
  </I>
);
export const CloseIcon = (p: IconProps) => (
  <I {...p}>
    <path d="M18 6 6 18M6 6l12 12" />
  </I>
);
export const MenuIcon = (p: IconProps) => (
  <I {...p}>
    <path d="M3 12h18M3 6h18M3 18h18" />
  </I>
);
export const SearchIcon = (p: IconProps) => (
  <I {...p}>
    <circle cx="11" cy="11" r="8" />
    <path d="m21 21-4.3-4.3" />
  </I>
);
export const BellIcon = (p: IconProps) => (
  <I {...p}>
    <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9M13.73 21a2 2 0 0 1-3.46 0" />
  </I>
);
// R-346 §21 — the top bar's compact "⋯" overflow. Three dots, drawn as dots
// rather than as a text ellipsis so it inherits stroke width and colour like every
// other icon here (design-system.md: no inline glyphs, they stop following).
export const MoreHorizontalIcon = (p: IconProps) => (
  <I {...p}>
    <circle cx="5" cy="12" r="1.4" />
    <circle cx="12" cy="12" r="1.4" />
    <circle cx="19" cy="12" r="1.4" />
  </I>
);
export const SunIcon = (p: IconProps) => (
  <I {...p}>
    <circle cx="12" cy="12" r="4" />
    <path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M6.34 17.66l-1.41 1.41M19.07 4.93l-1.41 1.41" />
  </I>
);
export const MoonIcon = (p: IconProps) => (
  <I {...p}>
    <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
  </I>
);

// ── status ───────────────────────────────────────────────────────────────────
export const AlertTriangleIcon = (p: IconProps) => (
  <I {...p}>
    <path d="M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0Z" />
    <path d="M12 9v4M12 17h.01" />
  </I>
);
export const AlertCircleIcon = (p: IconProps) => (
  <I {...p}>
    <circle cx="12" cy="12" r="9" />
    <path d="M12 8v4M12 16h.01" />
  </I>
);

// ── sensor channels ──────────────────────────────────────────────────────────
export const ThermometerIcon = (p: IconProps) => (
  <I {...p}>
    <path d="M14 14.76V3.5a2.5 2.5 0 0 0-5 0v11.26a4.5 4.5 0 1 0 5 0z" />
  </I>
);
export const CloudIcon = (p: IconProps) => (
  <I {...p}>
    <path d="M18 10h-1.26A8 8 0 1 0 9 20h9a5 5 0 0 0 0-10z" />
  </I>
);
export const WavesIcon = (p: IconProps) => (
  <I {...p}>
    <path d="M2 8c2-2 4-2 6 0s4 2 6 0 4-2 6 0M2 14c2-2 4-2 6 0s4 2 6 0 4-2 6 0" />
  </I>
);
export const TruckIcon = (p: IconProps) => (
  <I {...p}>
    <rect x="1" y="5" width="14" height="12" rx="1" />
    <path d="M15 8h4l3 3v6h-7" />
    <circle cx="6" cy="19" r="2" />
    <circle cx="18" cy="19" r="2" />
  </I>
);
export const BarChartIcon = (p: IconProps) => (
  <I {...p}>
    <line x1="6" y1="20" x2="6" y2="10" />
    <line x1="12" y1="20" x2="12" y2="4" />
    <line x1="18" y1="20" x2="18" y2="14" />
  </I>
);

// ── actuator channels ────────────────────────────────────────────────────────
export const WindIcon = (p: IconProps) => (
  <I {...p}>
    <path d="M17.7 7.7a2.5 2.5 0 1 1 1.8 4.3H2" />
    <path d="M9.6 4.6A2 2 0 1 1 11 8H2" />
    <path d="M12.6 19.4A2 2 0 1 0 14 16H2" />
  </I>
);
export const LightIcon = (p: IconProps) => (
  <I {...p}>
    <path d="M9 18h6M10 21h4M12 3a6 6 0 0 0-4 10.5c.6.6 1 1.3 1 2.5h6c0-1.2.4-1.9 1-2.5A6 6 0 0 0 12 3z" />
  </I>
);
export const PumpIcon = (p: IconProps) => (
  <I {...p}>
    <circle cx="12" cy="12" r="8" />
    <path d="M12 8v4l3 2" />
  </I>
);
export const ValveIcon = (p: IconProps) => (
  <I {...p}>
    <path d="M4 12h16M12 4v16M7 7l10 10M17 7 7 17" />
  </I>
);
export const BoltIcon = (p: IconProps) => (
  <I {...p}>
    <path d="M13 2 4 14h7l-1 8 9-12h-7z" />
  </I>
);

export function ShieldIcon(props: IconProps) {
  return (
    <I {...props}>
      <path d="M12 22s8-3.5 8-10V5l-8-3-8 3v7c0 6.5 8 10 8 10z" />
      <path d="M9 12l2 2 4-4" />
    </I>
  );
}

// ── intake / scanning ────────────────────────────────────────────────────────
export const KeyboardIcon = (p: IconProps) => (
  <I {...p}>
    <rect x="2.5" y="6" width="19" height="12" rx="2" />
    <path d="M6 10h.01M9.5 10h.01M13 10h.01M16.5 10h.01M7 14h10" />
  </I>
);

export const LockIcon = (p: IconProps) => (
  <I {...p}>
    <rect x="5" y="11" width="14" height="9" rx="2" />
    <path d="M8 11V8a4 4 0 0 1 8 0v3" />
  </I>
);

export const QrCodeIcon = (p: IconProps) => (
  <I {...p}>
    <rect x="3" y="3" width="7" height="7" rx="1" />
    <rect x="14" y="3" width="7" height="7" rx="1" />
    <rect x="3" y="14" width="7" height="7" rx="1" />
    <path d="M14 14h3v3h-3zM20 14v.01M14 20h.01M17 20h4v-3" />
  </I>
);
